/**
 * Gate Page
 * Base layout for all gate types with shared UI logic
 * Supports both play mode and review mode (for viewing completed quest results)
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTokens } from '../../contexts/TokenContext';
import {
  GateType,
  GateEvaluationResult,
  TranslationQuestion,
  CombineQuestion,
  ExpansionQuestion,
  EssayQuestion,
  GateReviewData,
  GATE_CONFIGS,
  UserLearningData,
  VocabularyAnalysis,
} from '../../types/writing';
import {
  getGateQuestions,
  submitGateAnswer,
  completeGate,
  getGateReview,
  retryCourseQuest,
  getUserLearningData,
  analyzeVocabularyUsage,
  updateFamiliarity,
} from '../../services/writingService';
import { learningService } from '../../services/learningService';
import { Note } from '../../types/learning';
import NoteEditor from '../../components/learning/NoteEditor';
import TranslationGate from '../../components/writing/TranslationGate';
import CombineGate from '../../components/writing/CombineGate';
import ExpansionGate from '../../components/writing/ExpansionGate';
import EssayGate from '../../components/writing/EssayGate';
import '../learning/SentenceNav.css';
import './GatePage.css';

const GatePage: React.FC = () => {
  const { courseId, gateType } = useParams<{ courseId: string; gateType: GateType }>();
  const navigate = useNavigate();
  const { t } = useTranslation(['writing', 'common']);
  const { refreshStats } = useTokens();

  const [questions, setQuestions] = useState<any[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [currentResult, setCurrentResult] = useState<GateEvaluationResult | null>(null);
  const [scores, setScores] = useState<number[]>([]);
  const scoresRef = React.useRef<number[]>([]);
  const [gateComplete, setGateComplete] = useState(false);
  const [lastSubmittedAnswer, setLastSubmittedAnswer] = useState('');
  const [showNoteEditor, setShowNoteEditor] = useState(false);
  const [prefilledNote, setPrefilledNote] = useState<{ content: string; tags: string[] } | null>(null);

  // Review mode state
  const [reviewMode, setReviewMode] = useState(false);
  const [reviewData, setReviewData] = useState<GateReviewData | null>(null);
  const [retrying, setRetrying] = useState(false);

  // Vocabulary analysis state (for essay/expansion gates)
  const [userLearningData, setUserLearningData] = useState<UserLearningData | null>(null);
  const [vocabularyAnalysis, setVocabularyAnalysis] = useState<VocabularyAnalysis | null>(null);
  const [familiarityUpdatePending, setFamiliarityUpdatePending] = useState(false);

  const config = GATE_CONFIGS.find(g => g.type === gateType);

  // Load questions - with fallback to review mode on 404
  const loadQuestions = useCallback(async () => {
    if (!courseId || !gateType) return;

    setLoading(true);
    setError(null);
    setReviewMode(false);
    setReviewData(null);

    try {
      const data = await getGateQuestions(courseId, gateType);
      setQuestions(data.questions);
    } catch (err: any) {
      // If 404, try to load review data (completed quest)
      if (err.response?.status === 404) {
        try {
          const review = await getGateReview(courseId, gateType);
          setReviewMode(true);
          setReviewData(review);
          setQuestions(review.questions);
        } catch (reviewErr: any) {
          setError(reviewErr.message || t('writing:gatePageError'));
        }
      } else {
        setError(err.message || t('writing:gatePageError'));
      }
    } finally {
      setLoading(false);
    }
  }, [courseId, gateType, t]);

  useEffect(() => {
    loadQuestions();
  }, [loadQuestions]);

  // Load user learning data when entering quest page (for vocabulary analysis)
  useEffect(() => {
    const loadLearningData = async () => {
      try {
        const data = await getUserLearningData();
        setUserLearningData(data);
      } catch (err) {
        console.error('Failed to load user learning data:', err);
      }
    };
    loadLearningData();
  }, []);

  // Submit answer
  const handleSubmit = async (answer: string) => {
    if (!courseId || !gateType || !questions[currentIndex]) return;

    setSubmitting(true);
    setCurrentResult(null);
    setLastSubmittedAnswer(answer);
    setVocabularyAnalysis(null);

    try {
      const result = await submitGateAnswer(
        courseId,
        gateType,
        questions[currentIndex].id,
        answer
      );
      setCurrentResult(result);
      const newScores = [...scoresRef.current, result.score];
      scoresRef.current = newScores;
      setScores(newScores);

      // For essay/expansion gates, analyze vocabulary usage
      if ((gateType === 'essay' || gateType === 'expansion') && userLearningData) {
        try {
          const currentQuestion = questions[currentIndex];
          const requiredVocab = gateType === 'expansion'
            ? (currentQuestion as ExpansionQuestion).requiredVocabulary || []
            : [];

          const analysis = await analyzeVocabularyUsage(
            answer,
            requiredVocab,
            userLearningData,
            gateType
          );
          setVocabularyAnalysis(analysis);

          // Mark that we need to update familiarity after modal closes
          if (analysis.familiarityChanges.length > 0) {
            setFamiliarityUpdatePending(true);
          }
        } catch (analysisErr) {
          console.error('Failed to analyze vocabulary:', analysisErr);
        }
      }
    } catch (err: any) {
      setError(err.message || t('common:failed'));
    } finally {
      setSubmitting(false);
    }
  };

  // Move to next question or complete gate
  const handleNext = async () => {
    if (currentIndex < questions.length - 1) {
      setCurrentIndex(currentIndex + 1);
      setCurrentResult(null);
      setVocabularyAnalysis(null);
    } else {
      // Gate complete - calculate final score and submit
      const currentScores = scoresRef.current;
      const finalScore = currentScores.length > 0
        ? Math.round(currentScores.reduce((a, b) => a + b, 0) / currentScores.length)
        : 0;

      try {
        if (courseId && gateType) {
          await completeGate(courseId, gateType, finalScore);
          await refreshStats();
          setGateComplete(true);
        }
      } catch (err: any) {
        setError(err.message || t('common:failed'));
      }
    }
  };

  // Navigate back to writing
  const handleBackToQuest = () => {
    navigate(`/courses/${courseId}/writing`);
  };

  // Handle retry quest (same questions)
  const handleRetryQuest = async () => {
    if (!courseId || retrying) return;

    setRetrying(true);
    try {
      await retryCourseQuest(courseId);
      await refreshStats();
      // Navigate to writing page to see reset progress
      navigate(`/courses/${courseId}/writing`);
    } catch (err: any) {
      setError(err.message || t('common:failed'));
      setRetrying(false);
    }
  };

  // Open NoteEditor with a single feedback item pre-filled
  const handleSaveItemToNotes = useCallback((item: string) => {
    if (!gateType) return;
    setPrefilledNote({
      content: item,
      tags: ['writing', gateType],
    });
    setShowNoteEditor(true);
  }, [gateType]);

  // Format feedback into note content and open NoteEditor
  const handleSaveToNotes = useCallback(() => {
    if (!currentResult || !gateType) return;

    const gateLabel = config?.nameChinese || gateType;
    const lines: string[] = [];

    lines.push(`[${gateLabel}] Score: ${currentResult.score}%`);
    lines.push('');
    lines.push('--- My Answer ---');
    lines.push(lastSubmittedAnswer);
    lines.push('');
    lines.push('--- Feedback ---');
    lines.push(currentResult.feedback);

    if (currentResult.grammarIssues && currentResult.grammarIssues.length > 0) {
      lines.push('');
      lines.push('--- Grammar Issues ---');
      currentResult.grammarIssues.forEach((issue, i) => {
        lines.push(`${i + 1}. ${issue}`);
      });
    }

    if (currentResult.suggestions && currentResult.suggestions.length > 0) {
      lines.push('');
      lines.push('--- Suggestions ---');
      currentResult.suggestions.forEach((s, i) => {
        lines.push(`${i + 1}. ${s}`);
      });
    }

    if (currentResult.improvedVersion) {
      lines.push('');
      lines.push('--- Improved Version ---');
      lines.push(currentResult.improvedVersion);
    }

    setPrefilledNote({
      content: lines.join('\n'),
      tags: ['writing', gateType],
    });
    setShowNoteEditor(true);
  }, [currentResult, gateType, lastSubmittedAnswer, config]);

  // Save note via learningService
  const handleNoteSave = async (noteData: Partial<Note>) => {
    if (!courseId) return;
    try {
      await learningService.createNote({
        courseId,
        content: noteData.content || '',
        tags: noteData.tags,
        relatedWord: noteData.relatedWord || undefined,
      });
      setShowNoteEditor(false);
      setPrefilledNote(null);
    } catch (err: any) {
      console.error('Failed to save note:', err);
    }
  };

  // Handle familiarity update after feedback modal closes
  const handleFamiliarityUpdate = useCallback(async () => {
    if (!familiarityUpdatePending || !vocabularyAnalysis?.familiarityChanges.length) return;

    try {
      await updateFamiliarity(vocabularyAnalysis.familiarityChanges);
      setFamiliarityUpdatePending(false);
      // Refresh user learning data to get updated familiarity scores
      const updatedData = await getUserLearningData();
      setUserLearningData(updatedData);
    } catch (err) {
      console.error('Failed to update familiarity:', err);
    }
  }, [familiarityUpdatePending, vocabularyAnalysis]);

  // Render the appropriate gate component
  const renderGateContent = () => {
    const currentQuestion = questions[currentIndex];
    if (!currentQuestion) return null;

    const commonProps = {
      question: currentQuestion,
      onSubmit: handleSubmit,
      submitting,
      result: currentResult,
      onSaveToNotes: currentResult ? handleSaveToNotes : undefined,
      onSaveItemToNotes: currentResult ? handleSaveItemToNotes : undefined,
    };

    switch (gateType) {
      case 'translation':
        return <TranslationGate {...commonProps} question={currentQuestion as TranslationQuestion} />;
      case 'combine':
        return <CombineGate {...commonProps} question={currentQuestion as CombineQuestion} />;
      case 'expansion':
        return (
          <ExpansionGate
            {...commonProps}
            question={currentQuestion as ExpansionQuestion}
            vocabularyAnalysis={vocabularyAnalysis || undefined}
            onFamiliarityUpdate={handleFamiliarityUpdate}
          />
        );
      case 'essay':
        return (
          <EssayGate
            {...commonProps}
            question={currentQuestion as EssayQuestion}
            vocabularyAnalysis={vocabularyAnalysis || undefined}
            onFamiliarityUpdate={handleFamiliarityUpdate}
          />
        );
      default:
        return <div>{t('writing:unknownGateType')}</div>;
    }
  };

  // Render review mode content
  const renderReviewContent = () => {
    if (!reviewData || !questions.length) return null;

    return (
      <div className="gate-review">
        {/* Review header */}
        <div className="gate-review-header">
          <span className="gate-review-header__badge">{t('reviewMode')}</span>
          <span className={`gate-review-header__score ${reviewData.isPassed ? 'pass' : 'fail'}`}>
            {reviewData.score}%
          </span>
        </div>

        {/* Review items */}
        <div className="gate-review-items">
          {questions.map((question, index) => {
            const userAnswer = reviewData.answers[index] || '';
            // Determine correctness based on question type
            let isCorrect = false;
            if (gateType === 'translation') {
              // Translation is AI-graded, use overall pass status
              isCorrect = reviewData.isPassed;
            } else if (gateType === 'combine' || gateType === 'expansion' || gateType === 'essay') {
              // For other types, consider passed if score >= 60
              isCorrect = reviewData.isPassed;
            }

            return (
              <div key={question.id || index} className="gate-review-item">
                <div className="gate-review-item__header">
                  <span className="gate-review-item__number">{t('question')} {index + 1}</span>
                  <span className={`gate-review-item__status ${isCorrect ? 'correct' : 'incorrect'}`}>
                    {isCorrect ? '✓' : '✗'}
                  </span>
                </div>

                {/* Question content based on type */}
                <div className="gate-review-item__question">
                  {gateType === 'translation' && (
                    <p className="gate-review-item__sentence">
                      {(question as TranslationQuestion).sourceText}
                    </p>
                  )}
                  {gateType === 'combine' && (
                    <div className="gate-review-item__sentences">
                      {Array.isArray((question as CombineQuestion).sentences) && (question as CombineQuestion).sentences?.map((s, i) => (
                        <p key={i}>{i + 1}. {typeof s === 'string' ? s : JSON.stringify(s)}</p>
                      ))}
                    </div>
                  )}
                  {gateType === 'expansion' && (
                    <p className="gate-review-item__seed">
                      {(question as ExpansionQuestion).seedSentence}
                    </p>
                  )}
                  {gateType === 'essay' && (
                    <p className="gate-review-item__topic">
                      {(question as EssayQuestion).topic}
                    </p>
                  )}
                </div>

                {/* User answer */}
                <div className="gate-review-item__answer">
                  <span className="gate-review-item__label">{t('yourAnswer')}:</span>
                  <p className={`gate-review-item__user-answer ${isCorrect ? 'correct' : 'incorrect'}`}>
                    {userAnswer || <em>{t('noAnswer')}</em>}
                  </p>
                </div>

                {/* Reference answer for translation */}
                {gateType === 'translation' && (
                  <div className="gate-review-item__correct">
                    <span className="gate-review-item__label">{t('referenceAnswer')}:</span>
                    <p className="gate-review-item__correct-value">
                      {(question as TranslationQuestion).referenceAnswer}
                    </p>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* AI Feedback summary */}
        {reviewData.aiFeedback && (
          <div className="gate-review-feedback">
            <h4 className="gate-review-feedback__title">{t('feedback')}</h4>

            {reviewData.aiFeedback.strengths?.length > 0 && (
              <div className="gate-review-feedback__section">
                <span className="gate-review-feedback__label">{t('strengths')}:</span>
                <ul className="gate-review-feedback__list">
                  {reviewData.aiFeedback.strengths.map((s, i) => (
                    <li key={i}>{s}</li>
                  ))}
                </ul>
              </div>
            )}

            {reviewData.aiFeedback.improvements?.length > 0 && (
              <div className="gate-review-feedback__section">
                <span className="gate-review-feedback__label">{t('areasToImprove')}:</span>
                <ul className="gate-review-feedback__list gate-review-feedback__list--improve">
                  {reviewData.aiFeedback.improvements.map((s, i) => (
                    <li key={i}>{s}</li>
                  ))}
                </ul>
              </div>
            )}

            {reviewData.aiFeedback.encouragement && (
              <p className="gate-review-feedback__encouragement">
                {reviewData.aiFeedback.encouragement}
              </p>
            )}
          </div>
        )}

        {/* Review actions */}
        <div className="gate-review-actions">
          <button
            className="gate-review-actions__retry"
            onClick={handleRetryQuest}
            disabled={retrying}
          >
            {retrying ? t('loading', { ns: 'common' }) : t('retryQuest')}
          </button>
          <button
            className="gate-review-actions__back"
            onClick={handleBackToQuest}
          >
            {t('backToQuest')}
          </button>
        </div>
      </div>
    );
  };

  // Loading state
  if (loading) {
    return (
      <div className="gate-page gate-page--loading">
        <div className="gate-loading">
          <div className="gate-loading__spinner" />
          <p>{t('loadingQuestions')}</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="gate-page gate-page--error">
        <div className="gate-error">
          <h3>{t('error', { ns: 'common' })}</h3>
          <p>{error}</p>
          <button onClick={loadQuestions} className="gate-error__retry">
            {t('retry', { ns: 'common' })}
          </button>
          <button onClick={handleBackToQuest} className="gate-error__back">
            {t('backToQuest')}
          </button>
        </div>
      </div>
    );
  }

  // Review mode - show readonly results
  if (reviewMode && reviewData) {
    return (
      <div className="gate-page gate-page--review">
        {/* Header */}
        <div className="learning-mode-header">
          <button className="quest-back-btn" onClick={handleBackToQuest}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5"/><path d="M12 19l-7-7 7-7"/></svg>
          </button>
          <h2 className="learning-mode-header__title">
            <span className="gate-title-icon">{config?.icon}</span>
            {config?.nameChinese || gateType}
          </h2>
          <span className="gate-review-badge">{t('reviewResults')}</span>
        </div>

        {/* Review content */}
        <section className="gate-content">
          {renderReviewContent()}
        </section>
      </div>
    );
  }

  // Gate complete state
  if (gateComplete) {
    const currentScores = scoresRef.current;
    const finalScore = currentScores.length > 0
      ? Math.round(currentScores.reduce((a, b) => a + b, 0) / currentScores.length)
      : 0;
    const passed = finalScore >= 60;

    return (
      <div className="gate-page gate-page--complete">
        <div className="gate-complete">
          <div className="gate-complete__icon">
            {passed ? (
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#667eea" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9H4.5a2.5 2.5 0 010-5H6"/><path d="M18 9h1.5a2.5 2.5 0 000-5H18"/><path d="M4 22h16"/><path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20 7 22"/><path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20 17 22"/><path d="M18 2H6v7a6 6 0 1012 0V2z"/></svg>
            ) : (
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#667eea" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z"/><path d="M8 14s1.5 2 4 2 4-2 4-2"/><line x1="9" y1="9" x2="9.01" y2="9"/><line x1="15" y1="9" x2="15.01" y2="9"/></svg>
            )}
          </div>
          <h2 className="gate-complete__title">
            {passed ? t('gatePassed') : t('gateFailed')}
          </h2>
          <div className="gate-complete__score">
            <span className={`gate-complete__score-value ${passed ? 'pass' : 'fail'}`}>
              {finalScore}%
            </span>
          </div>
          {passed && (
            <div className="gate-complete__rewards">
              <span className="gate-complete__xp">+{config?.xpReward || 0} XP</span>
            </div>
          )}
          <p className="gate-complete__message">
            {passed ? t('gatePassedMessage') : t('gateFailedMessage')}
          </p>
          <button className="gate-complete__btn" onClick={handleBackToQuest}>
            {t('continueJourney')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="gate-page">
      {/* Header */}
      <div className="learning-mode-header">
        <button className="quest-back-btn" onClick={handleBackToQuest}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5"/><path d="M12 19l-7-7 7-7"/></svg>
        </button>
        <h2 className="learning-mode-header__title">
          <span className="gate-title-icon">{config?.icon}</span>
          {config?.nameChinese || gateType}
        </h2>
        <div className="learning-mode-header__nav">
          <button
            className="sentence-nav-btn"
            disabled={true}
          >
            ◀
          </button>
          <span className="sentence-nav-counter">{currentIndex + 1} / {questions.length}</span>
          <button
            className="sentence-nav-btn"
            disabled={true}
          >
            ▶
          </button>
        </div>
      </div>

      {/* Progress bar */}
      <div className="gate-progress-bar">
        <div
          className="gate-progress-bar__fill"
          style={{ width: `${((currentIndex + (currentResult ? 1 : 0)) / questions.length) * 100}%` }}
        />
      </div>

      {/* Main content */}
      <section className="gate-content">
        {renderGateContent()}

        {/* Result and next button (shown after result, inside content area) */}
        {currentResult && (
          <div className="gate-inline-result">
            <div className="gate-result-summary">
              <span className={`gate-result-badge ${currentResult.passed ? 'pass' : 'fail'}`}>
                {currentResult.passed ? t('correct') : t('needsWork')}
              </span>
              <span className="gate-result-score">
                {currentResult.score}%
              </span>
            </div>
            <button className="gate-next-btn" onClick={handleNext}>
              {currentIndex < questions.length - 1 ? t('nextQuestion') : t('finishGate')}
            </button>
          </div>
        )}
      </section>

      {/* NoteEditor Modal */}
      {showNoteEditor && (
        <NoteEditor
          note={null}
          onSave={handleNoteSave}
          onClose={() => { setShowNoteEditor(false); setPrefilledNote(null); }}
          prefilledContent={prefilledNote}
        />
      )}
    </div>
  );
};

export default GatePage;
